import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, parseApiError, type SeedingStandingRow } from '../lib/api';
import { type ModeConfig } from '../lib/modes';
import { useEffectiveRole } from '../lib/viewAs';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { Trophy, ChevronDown, ChevronUp, Plus } from 'lucide-react';

// ── Seeding row with expandable breakdown ─────────────────────────────────────

function SeedingRow({
  row,
  rank,
  modes,
}: {
  row: SeedingStandingRow;
  rank: number;
  modes: ModeConfig[];
}) {
  const [expanded, setExpanded] = useState(false);

  const rankColor =
    rank === 1 ? 'text-ink-primary' :
    rank === 2 ? 'text-ink-body' :
    rank === 3 ? 'text-ink-secondary' :
    'text-ink-meta';

  const modeColCount = modes.length;

  return (
    <>
      <tr
        className="cursor-pointer hover:bg-white/[0.04] transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <td className="py-3 pl-4 pr-2 text-center">
          <span className={`font-mono text-sm font-bold tabular-nums ${rankColor}`}>{rank}</span>
        </td>
        <td className="py-3 px-3">
          <span className="font-medium text-ink-primary">{row.clanName}</span>
        </td>
        <td className="py-3 px-3 text-center tabular-nums text-sm text-ink-body">{row.roundsPlayed}</td>
        {modes.map(m => (
          <td key={m.key} className="py-3 px-3 text-center tabular-nums text-sm text-ink-secondary">
            {(row.modePoints?.[m.key] ?? 0).toLocaleString()}
          </td>
        ))}
        <td className="py-3 pl-3 pr-4 text-center">
          <span className="font-mono text-base font-bold tabular-nums text-accent-500">
            {(row.totalPoints ?? 0).toLocaleString()}
          </span>
        </td>
        <td className="py-3 pr-3 text-ink-meta">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </td>
      </tr>

      {expanded && row.roundBreakdown.length > 0 && (
        <tr>
          <td colSpan={4 + modeColCount} className="bg-white/[0.02] pb-3 pt-1">
            <div className="mx-4 space-y-1">
              <p className="mb-2 font-display text-xs font-semibold uppercase tracking-wider text-ink-meta">Round Breakdown</p>
              {row.roundBreakdown
                .sort((a, b) => a.roundNumber - b.roundNumber)
                .map(r => (
                  <div
                    key={r.roundNumber}
                    className="flex items-center justify-between rounded-lg bg-white/[0.04] px-3 py-2 text-xs"
                  >
                    <span className="text-ink-secondary">
                      Round {r.roundNumber} <span className="text-ink-meta">vs</span>{' '}
                      <span className="text-ink-primary">{r.opponent}</span>
                    </span>
                    <div className="flex items-center gap-4">
                      {modes.map(m => {
                        const score = r.modeScores?.[m.key];
                        if (score === undefined || score === 0) return null;
                        return (
                          <span key={m.key} className="text-ink-secondary">
                            {m.shortLabel}: {score.toLocaleString()}
                          </span>
                        );
                      })}
                      <span className="font-mono font-semibold text-ink-primary">
                        +{(r.pointsEarned ?? 0).toLocaleString()} pts
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Submit seeding round form ─────────────────────────────────────────────────

function SubmitSeedingForm({ compId, compName, roundsTotal, modes }: { compId: string; compName: string; roundsTotal: number; modes: ModeConfig[] }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [opponent, setOpponent] = useState('');
  const [round, setRound] = useState(1);
  const [scores, setScores] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const { data: clans } = useQuery({
    queryKey: ['competitionClans', compId],
    queryFn: () => api.competitionClans(compId),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () => {
      const modeScores: Record<string, number> = {};
      for (const m of modes) {
        const val = scores[m.key];
        if (val) modeScores[m.key] = Number(val);
      }
      return api.submitSeedingRound({
        compId,
        opponentClan: opponent,
        roundNumber: round,
        modeScores,
      });
    },
    onSuccess: () => {
      setOpen(false);
      setOpponent(''); setScores({}); setError('');
      queryClient.invalidateQueries({ queryKey: ['seeding', compId] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className={cn('btn-primary inline-flex items-center gap-1.5', MORPH)}>
        <Plus className="h-4 w-4" /> Submit Round
      </button>
    );
  }

  return (
    <div className="mt-4 rounded-lg bg-white/[0.04] p-4 space-y-3">
      <p className="font-display text-xs font-semibold uppercase tracking-wider text-ink-meta">Submit Seeding Round — {compName}</p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Round #</label>
          <select value={round} onChange={e => setRound(Number(e.target.value))} className="input w-full">
            {Array.from({ length: roundsTotal }, (_, i) => i + 1).map(n => (
              <option key={n} value={n}>Round {n}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Opponent Clan</label>
          <select value={opponent} onChange={e => setOpponent(e.target.value)} className="input w-full">
            <option value="">Select opponent…</option>
            {(clans || []).map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${modes.length}, 1fr)` }}>
        {modes.map(m => (
          <div key={m.key}>
            <label className="label">{m.shortLabel} {m.seedingUnitLabel ? `(${m.seedingUnitLabel}s)` : ''}</label>
            <input type="number" placeholder="0" value={scores[m.key] ?? ''} min={0}
              onChange={e => setScores(s => ({ ...s, [m.key]: e.target.value }))}
              className="input w-full" />
          </div>
        ))}
      </div>

      {error && <p className="text-xs text-red-400">{error}</p>}

      <div className="flex gap-2">
        <button onClick={() => mutation.mutate()} disabled={!opponent || mutation.isPending}
          className={cn('btn-primary flex-1', MORPH)}>
          {mutation.isPending ? 'Submitting…' : 'Submit'}
        </button>
        <button onClick={() => setOpen(false)} className="btn-ghost">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── SeedingStandings page ─────────────────────────────────────────────────────

export function SeedingStandings() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const { data: comps } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });
  const [selectedCompId, setSelectedCompId] = useState('');

  const compId = selectedCompId || comps?.find(c => c.stage === 'SEEDING' || c.status === 'LIVE')?.id || comps?.[0]?.id || '';

  const { data, isLoading } = useQuery({
    queryKey: ['seeding', compId],
    queryFn: () => api.seeding(compId),
    enabled: !!compId,
  });

  // View-as-aware role — the submit gate reads THIS, not the raw /me role, so
  // a founder previewing member/public loses the round-submission form rather
  // than keeping a live write into a real comp's seeding table. Identity
  // no-op whenever no view-as is active — see lib/viewAs.ts.
  const effectiveRole = useEffectiveRole(me?.role);
  const isLeaderOrAdmin = effectiveRole === 'leader' || effectiveRole === 'admin' || effectiveRole === 'founder';

  const deadline = data?.seedingDeadline;
  const deadlinePassed = deadline && Date.now() > deadline;
  const deadlineLabel = deadline
    ? new Date(deadline).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;

  const modes = data?.modes ?? [];

  return (
    <div className="max-w-4xl">
      <div className={cn('mb-6 flex flex-wrap items-center justify-between gap-4', SETTLE)} style={settleDelay(0)}>
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-semibold text-ink-primary">
            <Trophy className="h-6 w-6 text-ink-secondary" /> Seeding Standings
          </h1>
          <p className="mt-1 text-sm text-ink-meta">
            {data?.seedingRoundsTotal ?? 5} rounds — teams ranked highest to lowest for division placement
          </p>
        </div>

        {comps && comps.length > 1 && (
          <select
            value={compId}
            onChange={e => setSelectedCompId(e.target.value)}
            className="input"
          >
            {comps.map(c => (
              <option key={c.id} value={c.id}>{c.name || c.id.replace('COMP#', '')}</option>
            ))}
          </select>
        )}
      </div>

      {/* Deadline + scoring info */}
      {data && modes.length > 0 && (
        <div className={cn('mb-6', SETTLE)} style={settleDelay(1)}>
          <Card>
            <div className="grid grid-cols-2 gap-4 text-sm" style={{ gridTemplateColumns: `repeat(${2 + modes.length}, 1fr)` }}>
              <div>
                <p className="text-xs uppercase tracking-wider text-ink-meta">Stage</p>
                <p className="font-semibold text-ink-primary">{data.stage ?? 'Pre-seeding'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-ink-meta">Rounds total</p>
                <p className="font-semibold text-ink-primary">{data.seedingRoundsTotal}</p>
              </div>
              {modes.map(m => (
                <div key={m.key}>
                  <p className="text-xs uppercase tracking-wider text-ink-meta">{m.shortLabel} pts/{m.seedingUnitLabel ?? 'unit'}</p>
                  <p className="font-mono font-semibold text-ink-body">{m.seedingPointsPerUnit ?? 0} pts</p>
                </div>
              ))}
            </div>
            {deadlineLabel && (
              <div className={`mt-3 rounded-lg px-3 py-2 text-xs ${deadlinePassed ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`}>
                {deadlinePassed ? `Seeding closed — deadline was ${deadlineLabel}` : `Seeding deadline: ${deadlineLabel}`}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Submit form — leaders only, deadline not passed */}
      {isLeaderOrAdmin && data && !deadlinePassed && compId && modes.length > 0 && (
        <div className={cn('mb-6', SETTLE)} style={settleDelay(2)}>
          <Card>
            <SubmitSeedingForm compId={compId} compName={data.compName} roundsTotal={data.seedingRoundsTotal} modes={modes} />
          </Card>
        </div>
      )}

      {/* Standings table */}
      <div className={SETTLE} style={settleDelay(3)}>
        <Card>
          {isLoading ? (
            <PageSpinner />
          ) : !data || data.standings.length === 0 ? (
            <p className="py-4 text-center text-sm text-ink-meta">
              No seeding scores yet. Rounds start rolling in once clans play their first matches.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-ink-meta">
                    <th className="pb-3 pl-4 pr-2 text-center">#</th>
                    <th className="pb-3 px-3">Clan</th>
                    <th className="pb-3 px-3 text-center">Rounds</th>
                    {modes.map(m => (
                      <th key={m.key} className="pb-3 px-3 text-center">{m.shortLabel}</th>
                    ))}
                    <th className="pb-3 pl-3 pr-4 text-center text-accent-500">Total</th>
                    <th className="pb-3 pr-3" />
                  </tr>
                </thead>
                <tbody>
                  {data.standings.map((row, i) => (
                    <SeedingRow
                      key={row.clanName}
                      row={row}
                      rank={i + 1}
                      modes={modes}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
